class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this._romerollCartBusy = false;

    this.addEventListener("keyup", (evt) => evt.code === "Escape" && this.close());

    const initialOverlay = this.querySelector("#CartDrawer-Overlay");
    if (initialOverlay) {
      initialOverlay.addEventListener("click", this.close.bind(this));
    }

    /*
     * ROMEROLL - CONTROL AUTORITATIVO DEL CARRITO DRAWER
     * -------------------------------------------------
     * Interceptamos +, -, y tachito ANTES de los handlers viejos del tema.
     * Así evitamos el bug donde sólo cambia visualmente el input pero
     * Shopify no repinta precio, cantidad total y footer.
     *
     * El request se hace directamente a /cart/change.js y después se
     * reemplaza TODO .drawer__inner con la sección nueva renderizada
     * por Shopify. El <cart-drawer> exterior NO se reemplaza y conserva
     * la clase active, por lo que el Drawer no se cierra.
     */
    this.addEventListener(
      "click",
      (event) => {
        const removeElement = event.target.closest("cart-remove-button");
        const quantityButton = event.target.closest(
          "quantity-input.cart-quantity .quantity__button, .cart-quantity .quantity__button"
        );

        if (!removeElement && !quantityButton) return;

        // Somos el único handler de estos controles dentro del Drawer.
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        if (this._romerollCartBusy) return;

        if (removeElement) {
          const line = parseInt(removeElement.dataset.index || "0", 10);
          if (line > 0) {
            this.romerollChangeCartLine(line, 0);
          }
          return;
        }

        const quantityInput = quantityButton
          .closest("quantity-input")
          ?.querySelector(".quantity__input");

        if (!quantityInput) return;

        const line = parseInt(quantityInput.dataset.index || "0", 10);
        const current = parseInt(quantityInput.value || "1", 10);
        const step = parseInt(quantityInput.getAttribute("step") || "1", 10);
        const min = parseInt(
          quantityInput.getAttribute("min") ||
            quantityInput.dataset.min ||
            "1",
          10
        );
        const maxAttr = quantityInput.getAttribute("max");
        const max = maxAttr ? parseInt(maxAttr, 10) : null;

        if (!line || Number.isNaN(current)) return;

        let nextQuantity = current;

        if (quantityButton.name === "minus") {
          nextQuantity = Math.max(min, current - step);
        } else if (quantityButton.name === "plus") {
          nextQuantity = current + step;
          if (max !== null && !Number.isNaN(max)) {
            nextQuantity = Math.min(max, nextQuantity);
          }
        } else {
          return;
        }

        if (nextQuantity === current) return;

        this.romerollChangeCartLine(line, nextQuantity);
      },
      true
    );

    this.setHeaderCartIconAccessibility();
    this.bindUpsellButton();

    // Also fix the badge that Liquid rendered on the initial page load.
    requestAnimationFrame(() => this.romerollStyleFloatingCount());
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    if (!cartLink) return;

    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  bindUpsellButton() {
    this.addEventListener("click", (event) => {
      const upsellButton = event.target.closest(".upsell-add-button");
      if (upsellButton) {
        event.preventDefault();

        const form = upsellButton.closest(".upsell-form");
        const variantId = form.querySelector('input[name="id"]').value;

        this.addItemToCart(variantId);
      }
    });
  }

  async addItemToCart(variantId) {
    const sections = this.getSectionsToRender().map((section) => section.id);

    const body = JSON.stringify({
      id: variantId,
      quantity: 1,
      sections: sections,
      sections_url: window.location.pathname,
    });

    try {
      const response = await fetch(`${routes.cart_add_url}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: `application/json`,
        },
        body,
      });

      if (response.status === 422) {
        const errorData = await response.json();
        // Handle error (e.g., show a notification)
        console.error("Error adding item to cart:", errorData.description);
        return;
      }

      const parsedState = await response.json();
      this.renderContents(parsedState);
    } catch (error) {
      console.error("Error adding item to cart:", error);
    }
  }

  /**
   * Refresh cart drawer after AJAX cart operations
   * Can be called directly or will be triggered by 'cart:ajax-update' event
   * @param {Object} sectionsData - Optional sections data from cart response
   */
  async refreshCartDrawer(sectionsData = null) {
    if (sectionsData && sectionsData.sections) {
      const dataToRender = {
        ...sectionsData,
        id: sectionsData.id || this.productId || null,
        item_count: sectionsData.item_count || 0
      };
      this.renderContents(dataToRender);
      return;
    }

    try {
      const sections = this.getSectionsToRender().map((section) => section.id);
      const url = `${window.location.pathname}?sections=${sections.join(',')}`;
      const response = await fetch(url);
      
      if (!response.ok) {
        return;
      }

      const parsedState = await response.json();
      
      // Wrap return in sections object for renderContents
      let sectionsData = null;
      if (parsedState.sections) {
        sectionsData = parsedState.sections;
      } else {
        sectionsData = {};
        const sectionIds = this.getSectionsToRender().map((section) => section.id);
        sectionIds.forEach((sectionId) => {
          if (parsedState[sectionId]) {
            sectionsData[sectionId] = parsedState[sectionId];
          }
        });
      }

      if (!sectionsData || Object.keys(sectionsData).length === 0) {
        console.error("No sections found in response:", parsedState);
        return;
      }

      const dataToRender = {
        sections: sectionsData,
        id: parsedState.id || this.productId || null,
        item_count: parsedState.item_count || 0
      };
      
      this.renderContents(dataToRender);
    } catch (error) {
      console.error("Error refreshing cart drawer:", error);
    }
  }

  startCountdownTimer() {
    const timerElement = document.getElementById("cart-countdown-timer");
    if (!timerElement) return;

    // Reset timer to 10 minutes
    let minutes = 10;
    let seconds = 0;

    // Clear any existing timer
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }

    // Update the timer display
    const updateTimer = () => {
      timerElement.textContent = `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
    };

    // Initial display
    updateTimer();

    // Start the countdown
    this.countdownInterval = setInterval(() => {
      if (seconds === 0) {
        if (minutes === 0) {
          clearInterval(this.countdownInterval);
          return;
        }
        minutes--;
        seconds = 59;
      } else {
        seconds--;
      }

      updateTimer();
    }, 1000);
  }

  romerollSetControlsBusy(isBusy) {
    this._romerollCartBusy = isBusy;

    this.querySelectorAll(
      "quantity-input.cart-quantity .quantity__button, .cart-quantity .quantity__button, cart-remove-button .cart-remove-button"
    ).forEach((button) => {
      button.disabled = isBusy;
      button.setAttribute("aria-busy", isBusy ? "true" : "false");
    });
  }

  romerollStyleFloatingCount() {
    const floating = document.getElementById("romeroll-cart-fixed");
    if (!floating) return;

    const badge = floating.querySelector(".romeroll-cart-number");
    if (!badge) return;

    // ROMEROLL: keep the quantity badge visually attached INSIDE
    // the floating cart button instead of hanging outside it.
    badge.style.setProperty("top", "2px", "important");
    badge.style.setProperty("right", "2px", "important");
    badge.style.setProperty("min-width", "15px", "important");
    badge.style.setProperty("height", "15px", "important");
    badge.style.setProperty("padding", "0 3px", "important");
    badge.style.setProperty("font-size", "9px", "important");
    badge.style.setProperty("line-height", "1", "important");
    badge.style.setProperty("box-sizing", "border-box", "important");
    badge.style.setProperty("z-index", "2", "important");
  }

  romerollUpdateFloatingCount(itemCount) {
    const floating = document.getElementById("romeroll-cart-fixed");
    if (!floating) return;

    let badge = floating.querySelector(".romeroll-cart-number");

    if (itemCount > 0) {
      if (!badge) {
        badge = document.createElement("span");
        badge.className = "romeroll-cart-number";
        floating.appendChild(badge);
      }

      badge.textContent = itemCount;
      this.romerollStyleFloatingCount();
    } else if (badge) {
      badge.remove();
    }
  }

  romerollReplaceHeaderCartBubble(sectionHtml, itemCount) {
    const currentBubble = document.querySelector("#cart-icon-bubble");
    if (!currentBubble || !sectionHtml) {
      this.romerollUpdateFloatingCount(itemCount);
      return;
    }

    const doc = new DOMParser().parseFromString(sectionHtml, "text/html");
    const newBubble = doc.querySelector("#cart-icon-bubble");

    if (newBubble) {
      const iconType = currentBubble.getAttribute("data-cart-icon-type");
      currentBubble.innerHTML = newBubble.innerHTML;

      if (iconType) {
        currentBubble.setAttribute("data-cart-icon-type", iconType);
      }
    }

    this.romerollUpdateFloatingCount(itemCount);
  }

  async romerollChangeCartLine(line, quantity) {
    if (this._romerollCartBusy) return;

    this.romerollSetControlsBusy(true);

    try {
      const changeUrl =
        window.routes && window.routes.cart_change_url
          ? `${window.routes.cart_change_url}.js`
          : "/cart/change.js";

      const response = await fetch(changeUrl, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({
          line,
          quantity,
          sections: ["cart-drawer", "cart-icon-bubble"],
          sections_url: window.location.pathname,
        }),
      });

      if (!response.ok) {
        throw new Error(`Cart change failed: ${response.status}`);
      }

      const state = await response.json();

      if (state.errors) {
        throw new Error(
          typeof state.errors === "string"
            ? state.errors
            : JSON.stringify(state.errors)
        );
      }

      const cartSectionHtml = state.sections?.["cart-drawer"];

      if (!cartSectionHtml) {
        throw new Error("Shopify did not return the cart-drawer section.");
      }

      const doc = new DOMParser().parseFromString(
        cartSectionHtml,
        "text/html"
      );

      const newInner =
        doc.querySelector("#CartDrawer .drawer__inner") ||
        doc.querySelector(".drawer__inner");

      const currentInner = this.querySelector("#CartDrawer .drawer__inner");

      if (!newInner || !currentInner) {
        throw new Error("Could not find Drawer inner HTML after cart update.");
      }

      // This is the key: replace the complete rendered Drawer content,
      // including line price, quantity, savings, footer totals and empty state.
      currentInner.replaceWith(newInner);

      this.classList.toggle("is-empty", Number(state.item_count || 0) === 0);

      // The outer custom element survives, so preserve the open state.
      this.classList.add("animate", "active");
      document.body.classList.add("overflow-hidden");

      const renderedInner = this.querySelector(".drawer__inner");
      if (renderedInner) {
        renderedInner.style.setProperty(
          "transform",
          "translateX(0)",
          "important"
        );
      }

      this.romerollReplaceHeaderCartBubble(
        state.sections?.["cart-icon-bubble"],
        Number(state.item_count || 0)
      );

      // Let theme/app code know that fresh cart markup is now present.
      this.dispatchEvent(
        new CustomEvent("cart-drawer:render", {
          bubbles: true,
          detail: { parsedState: state },
        })
      );

      this.dispatchEvent(
        new CustomEvent("cart-drawer:update", {
          bubbles: true,
          detail: {
            newTotalCount: String(state.item_count || 0),
          },
        })
      );
    } catch (error) {
      console.error("Romeroll cart update error:", error);
    } finally {
      // The old buttons may have been replaced. Enable all controls in
      // the newly-rendered Drawer and release the request lock.
      this._romerollCartBusy = false;

      this.querySelectorAll(
        "quantity-input.cart-quantity .quantity__button, .cart-quantity .quantity__button, cart-remove-button .cart-remove-button"
      ).forEach((button) => {
        button.disabled = false;
        button.removeAttribute("aria-busy");
      });
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role")) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add("animate", "active");
    });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");
        const focusElement = this.querySelector(".drawer__inner") || this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add("overflow-hidden");

    // Check if jQuery and slick are loaded before initializing
    if (typeof $ !== "undefined" && typeof $.fn.slick !== "undefined") {
      const $slider = $(".gbfrequently-bought-with-main-whole-slider");
      if ($slider.length > 0) {
        $slider.slick({
          infinite: true,
          autoplaySpeed: 1000,
          autoplay: false,
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
          dots: true,
        });
        $slider.css("opacity", "1");
      }
    }

    // Start the countdown timer when drawer opens
    this.startCountdownTimer();

    this.dispatchEvent(new CustomEvent("cart-drawer:open", {
      bubbles: true,
      detail: { triggeredBy }
    }));
  }

  close() {
    // Check if cart customization mode is active
    const cartCustomizationMode = document.querySelector("cart-drawer").classList.contains("customization-mode");
    if (cartCustomizationMode) {
      alert("Cart Customization Mode is active. Disable it in Theme Settings > Cart Drawer to allow closing.");
      return;
    }

    this.classList.remove("active");
    removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-hidden");

    // Clear the countdown timer when drawer closes
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute("aria-controls", cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute("aria-expanded", !event.currentTarget.closest("details").hasAttribute("open"));
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector(".drawer__inner").classList.contains("is-empty") &&
      this.querySelector(".drawer__inner").classList.remove("is-empty");
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      if (section.id == "cart-drawer" || section.selector == "#CartDrawer") {
        const newCartDrawer = this.getSectionDOM(parsedState.sections[section.id], section.selector);
        updateCartDrawer(sectionElement, newCartDrawer);
      } else {
        if (section.id === "cart-icon-bubble") {
          const iconType = sectionElement.getAttribute("data-cart-icon-type");
          sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
          if (iconType) {
            sectionElement.setAttribute("data-cart-icon-type", iconType);
          }
          const iconWrapper = sectionElement.querySelector(".svg-wrapper");
          if (iconWrapper && iconType) {
            const bagIcon = iconWrapper.querySelector(".cart-icon-bag");
            const cartIcon = iconWrapper.querySelector(".cart-icon-cart");
            const cartIconEmpty = iconWrapper.querySelector(".cart-icon-cart-empty");
            
            if (iconType === "bag") {
              if (bagIcon) bagIcon.style.display = "flex";
            } 
            else {
              const cartEmpty = parsedState.item_count === 0;
              if (cartEmpty && cartIconEmpty) {
                cartIconEmpty.style.display = "flex";
              } else if (cartIcon) {
                cartIcon.style.display = "flex";
              }
            }
          }
        } else {
          sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
        }
      }
    });

    setTimeout(() => {
      this.querySelector("#CartDrawer-Overlay").addEventListener("click", this.close.bind(this));
      
      this.dispatchEvent(new CustomEvent("cart-drawer:render", {
        bubbles: true,
        detail: { parsedState }
      }));
      
      this.open();
    });

    // Check if jQuery and slick are loaded before initializing
    if (typeof $ !== "undefined" && typeof $.fn.slick !== "undefined") {
      const $slider = $(".gbfrequently-bought-with-main-whole-slider");
      if ($slider.length > 0) {
        $slider.slick({
          infinite: true,
          autoplaySpeed: 1000,
          autoplay: false,
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
          dots: true,
        });
        $slider.css("opacity", "1");
      }
    }
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    setTimeout(function () {
      // Check if jQuery and slick are loaded before initializing
      if (typeof $ !== "undefined" && typeof $.fn.slick !== "undefined") {
        var $slider = $(".gbfrequently-bought-with-main-whole-slider");
        if ($slider.length > 0 && !$slider.hasClass("slick-initialized")) {
          $slider.slick({
            infinite: true,
            autoplaySpeed: 1000,
            autoplay: false,
            slidesToShow: 1,
            slidesToScroll: 1,
            arrows: false,
            dots: true,
            // adaptiveHeight: true
            //prevArrow: $('.our-customer-slider-prev'),
            //nextArrow: $('.our-customer-slider-next'),
          });
          $slider.css("opacity", "1");
        }
      }
    }, 500);

    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawer);

function setupAjaxCartHandler() {
  const cartDrawer = document.querySelector("cart-drawer");
  if (!cartDrawer) return;

  document.addEventListener("cart:ajax-update", async function (event) {
    const sectionsData = event.detail?.sectionsData || null;
    await cartDrawer.refreshCartDrawer(sectionsData);
  });

  window.refreshCartDrawer = async function (sectionsData = null) {
    const drawer = document.querySelector("cart-drawer");
    if (drawer) {
      await drawer.refreshCartDrawer(sectionsData);
    }
  };
}

if (document.readyState === 'loading') {
  document.addEventListener("DOMContentLoaded", setupAjaxCartHandler);
} else {
  setupAjaxCartHandler();
}

// Wait for CartItems to be defined before extending it
if (typeof CartItems !== "undefined") {
  // CartItems is already loaded
  defineCartDrawerItems();
} else {
  // Wait for cart.js to load and define CartItems
  const checkCartItems = setInterval(() => {
    if (typeof CartItems !== "undefined") {
      clearInterval(checkCartItems);
      defineCartDrawerItems();
    }
  }, 50);
}

function defineCartDrawerItems() {
  if (customElements.get("cart-drawer-items")) {
    return; // Already defined
  }

  class CartDrawerItems extends CartItems {
    getSectionsToRender() {
      setTimeout(function () {
        // Check if jQuery and slick are loaded before initializing
        if (typeof $ !== "undefined" && typeof $.fn.slick !== "undefined") {
          const $slider = $(".gbfrequently-bought-with-main-whole-slider");
          if ($slider.length > 0) {
            $slider.slick({
              infinite: true,
              autoplaySpeed: 1000,
              autoplay: false,
              slidesToShow: 1,
              slidesToScroll: 1,
              arrows: false,
              dots: true,
            });
            $slider.css("opacity", "1");
          }
        }
      }, 500);

      return [
        {
          id: "CartDrawer",
          section: "cart-drawer",
          selector: ".drawer__inner",
        },
        {
          id: "cart-icon-bubble",
          section: "cart-icon-bubble",
          selector: ".shopify-section",
        },
      ];
    }
  }

  customElements.define("cart-drawer-items", CartDrawerItems);
}